import React from 'react';
import { motion } from 'framer-motion';
import Button from '../components/ui/Button';
import FadeIn from '../components/ui/FadeIn';
import SafeIcon from '../common/SafeIcon';
import Marquee from '../components/ui/Marquee';

const Home = () => {
  return (
    <>
      <section className="relative min-h-screen flex items-center pt-24 pb-0 overflow-hidden bg-brand-dark">
        <div className="absolute top-1/4 right-0 w-[60vw] h-[60vw] bg-brand-accent/20 rounded-full blur-[150px] -translate-y-1/2 translate-x-1/3 opacity-60 mix-blend-screen"></div>
        <div className="absolute bottom-0 left-0 w-[50vw] h-[50vw] bg-brand-rose/10 rounded-full blur-[120px] translate-y-1/3 -translate-x-1/4 opacity-50 mix-blend-screen"></div>
        
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full text-center z-0 pointer-events-none overflow-hidden">
          <h1 className="text-[15vw] font-serif font-bold text-outline opacity-20 whitespace-nowrap">ELEVATE</h1>
        </div>

        <div className="container mx-auto px-6 max-w-7xl relative z-10 h-full flex flex-col justify-center">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center mt-12">
            
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="lg:col-span-7"
            >
              <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-md text-white text-xs font-semibold tracking-[0.2em] uppercase mb-8">
                <span className="w-2 h-2 rounded-full bg-brand-accent animate-pulse"></span>
                Premium Business Mentoring
              </div>
              
              <h1 className="text-5xl md:text-7xl lg:text-[5.5rem] font-serif leading-[1.05] mb-8 text-white">
                Sichtbar werden. <br/>
                <span className="text-gradient italic font-light">Erfolgreich wachsen.</span>
              </h1>
              
              <p className="text-lg md:text-xl text-white/70 mb-12 leading-relaxed max-w-xl font-light">
                Rukiye Amini bringt dein Beauty-Business auf das nächste Level. Mehr Reichweite, exklusives Auftreten und eine glasklare Strategie für messbaren Premium-Umsatz.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-5">
                <Button to="/kontakt" variant="primary" className="!bg-brand-accent !text-white hover:!bg-brand-dark">
                  Erstgespräch sichern
                </Button>
                <Button to="/pakete" variant="darkGlass" showIcon={false}>
                  Leistungen entdecken
                </Button>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, scale: 0.9, rotate: -2 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 1.2, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="hidden lg:block lg:col-span-5 relative"
            >
              <div className="relative w-full aspect-[3/4] rounded-t-[10rem] rounded-b-3xl overflow-hidden border border-white/10 shadow-2xl z-10">
                <img 
                  src="https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?auto=format&fit=crop&q=80" 
                  alt="Glow Consulting" 
                  className="w-full h-full object-cover object-center scale-105 hover:scale-100 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/80 via-transparent to-transparent"></div>
              </div>
              
              <div className="absolute -left-12 bottom-20 glass-dark p-5 rounded-3xl animate-float z-20 flex items-center gap-5 border border-brand-accent/20">
                <div className="w-14 h-14 rounded-full bg-brand-accent flex items-center justify-center text-white">
                  <SafeIcon name="TrendingUp" className="w-7 h-7" />
                </div>
                <div>
                  <p className="text-[10px] text-brand-accent font-bold uppercase tracking-widest">Wachstum</p>
                  <p className="text-xl font-serif text-white mt-1">+120% Ø Umsatz</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee items={[
        "Beauty Business Mentoring",
        "Instagram Strategie",
        "Premium Positionierung",
        "Umsatzsteigerung",
        "Mindset & Empowerment"
      ]} />

      <section className="py-32 bg-brand-cream relative">
        <div className="container mx-auto px-6 max-w-7xl">
          <FadeIn className="flex flex-col md:flex-row justify-between items-end gap-8 mb-20 border-b border-brand-dark/10 pb-10">
            <div className="max-w-2xl">
              <h2 className="text-4xl md:text-6xl font-serif mb-6 leading-tight">Dein Business. <br/>Nur viel <span className="italic text-brand-accent">professioneller.</span></h2>
            </div>
            <p className="text-brand-text text-lg max-w-md md:text-right font-light">
              Warum bleiben so viele talentierte Frauen unter ihren Möglichkeiten? Oft fehlt nicht das Können, sondern die richtige Präsentation.
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-6 auto-rows-[minmax(300px,auto)]">
            <FadeIn delay={0.1} className="md:col-span-2 bg-brand-dark text-white rounded-[2.5rem] p-10 md:p-14 overflow-hidden relative group">
              <div className="absolute top-0 right-0 w-64 h-64 bg-brand-accent/20 rounded-full blur-[80px] group-hover:bg-brand-accent/40 transition-colors duration-700"></div>
              <div className="relative z-10 w-full md:w-2/3 h-full flex flex-col justify-center">
                <div className="w-16 h-16 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center border border-white/10 mb-8 text-brand-accent">
                  <SafeIcon name="Instagram" className="w-8 h-8" />
                </div>
                <h3 className="text-3xl md:text-4xl font-serif mb-5">Instagram, das verkauft</h3>
                <p className="text-white/70 leading-relaxed mb-10 font-light text-lg">Wir machen aus deinem Profil ein echtes Aushängeschild. Klare Botschaften, hochwertiges Design & Strategien, die Follower in Kundinnen verwandeln.</p>
                <div>
                  <Button to="/leistungen" variant="primary" className="!bg-brand-accent !text-white hover:!bg-brand-dark" showIcon={true}>Strategie ansehen</Button>
                </div>
              </div>
              <SafeIcon name="Smartphone" className="absolute -right-10 -bottom-10 w-80 h-80 text-white/5 group-hover:scale-110 group-hover:rotate-12 transition-transform duration-700 pointer-events-none" />
            </FadeIn>

            <FadeIn delay={0.2} className="bg-white rounded-[2.5rem] p-10 md:p-14 relative overflow-hidden group shadow-sm border border-brand-dark/5 hover:modern-shadow transition-shadow">
              <div className="relative z-10 h-full flex flex-col">
                <div className="w-16 h-16 bg-brand-cream rounded-2xl flex items-center justify-center mb-8 text-brand-dark">
                  <SafeIcon name="Star" className="w-8 h-8" />
                </div>
                <h3 className="text-3xl font-serif mb-4 text-brand-dark">Mindset & Mut</h3>
                <p className="text-brand-text leading-relaxed mt-auto font-light">Überwinde Selbstzweifel, präsentiere dich selbstbewusst und kommuniziere Premium-Preise stolz.</p>
              </div>
            </FadeIn>

            <FadeIn delay={0.3} className="bg-brand-rose rounded-[2.5rem] p-10 md:p-14 group relative overflow-hidden">
              <div className="relative z-10">
                <div className="w-16 h-16 bg-white/50 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-sm mb-8 text-brand-dark">
                  <SafeIcon name="Settings" className="w-8 h-8" />
                </div>
                <h3 className="text-3xl font-serif mb-4 text-brand-dark">Digitale Systeme</h3>
                <p className="text-brand-dark/70 leading-relaxed font-light">Automatisierte Terminbuchungen und smarte Tools, die dir massiv Zeit sparen.</p>
              </div>
              <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-white/20 rounded-full blur-3xl"></div>
            </FadeIn>

            <FadeIn delay={0.4} className="md:col-span-2 rounded-[2.5rem] overflow-hidden relative group h-80 md:h-auto">
              <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80" alt="Consulting" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/90 via-brand-dark/20 to-transparent flex items-end p-10 md:p-14">
                <h3 className="text-3xl md:text-5xl font-serif text-white">Dein Weg zur <br/><span className="text-brand-accent italic">Premium-Marke</span></h3>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="py-32 bg-brand-dark text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(ellipse_at_center,rgba(156,17,156,0.4)_0,transparent_60%)]"></div>
        <img src="https://images.unsplash.com/photo-1616394584738-fc6e612e71c9?auto=format&fit=crop&q=80" alt="Texture" className="absolute inset-0 w-full h-full object-cover opacity-10 mix-blend-overlay" />
        
        <div className="container mx-auto px-6 max-w-4xl text-center relative z-10">
          <FadeIn>
            <span className="text-brand-accent text-sm font-bold tracking-[0.2em] uppercase mb-6 block">Dein nächster Schritt</span>
            <h2 className="text-5xl md:text-7xl font-serif mb-8 leading-tight">Zeit, in deinen <br/><span className="italic">Erfolg</span> zu investieren.</h2>
            <p className="text-xl text-white/70 mb-12 max-w-2xl mx-auto font-light">Lass uns gemeinsam herausfinden, welche Hebel bei deinem Business den größten Unterschied machen.</p>
            <Button to="/kontakt" variant="primary" className="!bg-brand-accent !text-white hover:!bg-brand-dark text-lg !px-12 !py-6">
              Kostenloses Erstgespräch
            </Button>
          </FadeIn>
        </div>
      </section>
    </>
  );
};

export default Home;